import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface EngagementHealthRow {
  person_id: string;
  person_name: string;
  organization_id: string;
  score: number;
  attendance_component: number;
  giving_component: number;
  volunteering_component: number;
  care_component: number;
  engagement_tier: string;
}

export type EngagementHealthContext = {
  asOf: Date;
  members: EngagementHealthRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * PostgREST serialises NUMERIC columns as strings; without coercion, a single
 * unparseable upstream value poisons aggregates (NaN propagates through `+`).
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normalize an engagement_tier value for stable equality checks. Upstream tiers
 * may arrive with mixed case ("Engaged", "ENGAGED") or surrounding whitespace,
 * and case-sensitive `===` would silently drop those rows from the summary.
 * Null/undefined collapse to the table default of "inactive" — matching the
 * `engagement_scores.engagement_tier` column default and the wiring fallback.
 */
function normalizeTier(value: unknown): string {
  if (typeof value !== "string") return "inactive";
  const trimmed = value.trim().toLowerCase();
  return trimmed.length === 0 ? "inactive" : trimmed;
}

export function buildEngagementHealthAggregation(
  context: EngagementHealthContext,
): ReportAggregation {
  const members = Array.isArray(context.members) ? context.members : [];

  const reportRows = members.map((m) => {
    const rawName = typeof m.person_name === "string" ? m.person_name.trim() : "";
    return {
      id: m.person_id,
      label: rawName.length > 0 ? rawName : "Unknown Person",
      entityType: "person" as const,
      drilldownUrl: `/people/${encodeURIComponent(m.person_id)}`,
      attendanceComponent: toFiniteNumber(m.attendance_component),
      givingComponent: toFiniteNumber(m.giving_component),
      volunteeringComponent: toFiniteNumber(m.volunteering_component),
      careComponent: toFiniteNumber(m.care_component),
      engagementScore: toFiniteNumber(m.score),
      engagementTier: normalizeTier(m.engagement_tier),
    };
  });

  // Stable, locale-independent sort: highest engagement score first
  // (most-engaged members surface at the top), then by person name as a
  // deterministic tiebreaker for stable rendering across reloads, then by
  // person_id so identical names still produce a deterministic order.
  reportRows.sort((a, b) => {
    if (a.engagementScore !== b.engagementScore) {
      return b.engagementScore - a.engagementScore;
    }
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  // Single-pass aggregation. Tier comparisons go through `normalizeTier`
  // so "Engaged" / " engaged " / null all classify correctly. Score sum
  // goes through `toFiniteNumber` so a single NaN doesn't poison the
  // average (the divisor is `members.length`, so NaN scores are treated
  // as 0 contributions — they still count toward Members Tracked).
  const totalMembers = members.length;
  let engagedCount = 0;
  let inactiveCount = 0;
  let scoreSum = 0;
  for (const m of members) {
    const tier = normalizeTier(m.engagement_tier);
    if (tier === "engaged") engagedCount += 1;
    else if (tier === "inactive") inactiveCount += 1;
    scoreSum += toFiniteNumber(m.score);
  }
  const avgScore =
    totalMembers > 0 ? Math.round((scoreSum / totalMembers) * 100) / 100 : 0;

  return {
    asOf: context.asOf,
    summary: [
      { label: "Members Tracked", value: totalMembers },
      { label: "Actively Engaged", value: engagedCount },
      { label: "Inactive", value: inactiveCount },
      { label: "Avg Score", value: avgScore },
    ],
    rows: reportRows,
  };
}
